// src/Pages/PrivateFilters/PrivateFilters.js
// Admin page (Settings -> "الفلاتر الخاصة"): controls, per page and per filter,
// whether each list-page search filter is PUBLIC (shown to everyone) or PRIVATE
// (shown only to users holding the show_private_filters role).
// Gated by the show_private_filters role (route + menu); also guarded here.
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Paper, Typography, Tabs, Tab, Switch, Divider, Stack, Chip, CircularProgress
} from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import filterVisibilityAPI from '../../Network/FilterVisibility';
import { FILTER_REGISTRY } from './filterRegistry';

const PrivateFilters = () => {
    const rolesRedux = useSelector(state => state.rolesData.roles);
    const canManage = !!rolesRedux?.show_private_filters?.value;

    const [activeTab, setActiveTab] = useState(0);
    // visibility[pageKey][filterKey] = 'public' | 'private'  (absent => 'public')
    const [visibility, setVisibility] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null); // `${pageKey}::${filterKey}` being saved

    const activePage = FILTER_REGISTRY[activeTab] || FILTER_REGISTRY[0];

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await filterVisibilityAPI.getFilterVisibility({});
                const rows = res?.data?.data;
                const map = {};
                if (Array.isArray(rows)) {
                    rows.forEach(r => {
                        if (!r?.page_key || !r?.filter_key) return;
                        if (!map[r.page_key]) map[r.page_key] = {};
                        map[r.page_key][r.filter_key] = r.visibility;
                    });
                }
                if (active) setVisibility(map);
            } catch (e) {
                if (active) toast.error('تعذر تحميل اعدادات الفلاتر');
            }
            if (active) setLoading(false);
        })();
        return () => { active = false; };
    }, []);

    const getVisibility = (pageKey, filterKey) => visibility?.[pageKey]?.[filterKey] || 'public';

    const handleToggle = async (pageKey, filterKey, makePrivate) => {
        const nextVisibility = makePrivate ? 'private' : 'public';
        const prev = getVisibility(pageKey, filterKey);
        const saveKey = `${pageKey}::${filterKey}`;
        // optimistic update
        setVisibility(v => ({ ...v, [pageKey]: { ...(v?.[pageKey] || {}), [filterKey]: nextVisibility } }));
        setSavingKey(saveKey);
        try {
            const res = await filterVisibilityAPI.updateFilterVisibility({
                data: { page_key: pageKey, filter_key: filterKey, visibility: nextVisibility }
            });
            if (!res?.status || res?.data?.status === false) throw new Error('save failed');
            toast.success('تم الحفظ');
        } catch (e) {
            // revert on failure
            setVisibility(v => ({ ...v, [pageKey]: { ...(v?.[pageKey] || {}), [filterKey]: prev } }));
            toast.error('تعذر حفظ التغيير');
        }
        setSavingKey(null);
    };

    const privateCount = useMemo(() => {
        const m = visibility?.[activePage?.pageKey] || {};
        return Object.values(m).filter(v => v === 'private').length;
    }, [visibility, activePage]);

    if (!canManage) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography sx={{ fontFamily: 'Cairo-Bold' }}>لا تمتلك صلاحية عرض هذه الصفحة</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ fontFamily: 'Cairo-Bold', mb: 1 }}>
                التحكم بالفلاتر الخاصة
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Cairo-Medium', color: 'text.secondary', mb: 2 }}>
                الفلاتر العامة تظهر لجميع المستخدمين، اما الفلاتر الخاصة فتظهر فقط للمستخدمين الذين يمتلكون صلاحية "عرض الفلاتر الخاصة".
            </Typography>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { fontFamily: 'Cairo-Bold' } }}
                >
                    {FILTER_REGISTRY.map((p) => (
                        <Tab key={p.pageKey} label={p.pageLabel} />
                    ))}
                </Tabs>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : (
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <Chip size="small" label={`عدد الفلاتر: ${activePage?.filters?.length || 0}`} />
                            <Chip size="small" color="warning" label={`خاصة: ${privateCount}`} />
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        {(activePage?.filters || []).map((f) => {
                            const v = getVisibility(activePage.pageKey, f.key);
                            const isPrivate = v === 'private';
                            const saveKey = `${activePage.pageKey}::${f.key}`;
                            return (
                                <Stack
                                    key={f.key}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ py: 1, px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                                >
                                    <Typography sx={{ fontFamily: 'Cairo-Medium' }}>{f.label}</Typography>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography sx={{ fontFamily: 'Cairo-Medium', color: isPrivate ? 'warning.main' : 'success.main', minWidth: 44, textAlign: 'center' }}>
                                            {isPrivate ? 'خاص' : 'عام'}
                                        </Typography>
                                        <Switch
                                            color="warning"
                                            checked={isPrivate}
                                            disabled={savingKey === saveKey}
                                            onChange={(e) => handleToggle(activePage.pageKey, f.key, e.target.checked)}
                                        />
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default PrivateFilters;
